// home/Splash.tsx (GH4) - the boot/home surface. Matches the signed-off splash mockup (Joe 2026-07-13):
// the wordmark hero over a felt arc, then "Choose a game" (Cash $1/$3 active; Tournament honestly locked as
// a later phase, reusing the S26 Local/Pro honesty pattern), then "Sit down at" (New table = cold read, all
// seats "?"; Mid-session = drop into a diagnosed ring). Either choice deals a fresh engine hand on entry.
//
// All classes are namespaced `sp-*` on purpose: the shipped app has bitten twice on bare class-name
// collisions (.hero absolutely-positioned by felt.css, .badge/.tile/.card owned by other surfaces - see the
// WORKING_STATE gotchas), so the splash owns its own namespace and cannot be clobbered.
import { TABLE_SIZES, type TableSize } from '../live/table';
import type { TableMode } from './tableMode';
import {
  STAKES_POOLS,
  STAKES_ORDER,
  BUY_IN_DEPTHS,
  buyInFor,
  dollars,
  type StakesId,
} from './stakes';

/** The one-line honest reason to pick each size - 9 is the real $1/$3 game and the size the shipped preflop
 *  charts were authored for (North Star table-size amendment, 2026-07-15); the shorter tables are practice
 *  formats, and the copy says so rather than presenting three equal options. */
const SIZE_NOTE: Record<TableSize, string> = {
  6: 'Six-handed. Wider ranges, more blinds - an online-shaped table.',
  7: 'Seven-handed. A short live table, in between the two.',
  9: 'Full ring. The $1 / $3 game your room actually spreads.',
};

export interface SplashProps {
  /** sit down at the chosen Cash stakes/buy-in in the given mode → enter the live play spine. */
  onSit: (mode: TableMode) => void;
  /** ST-UI: sit down at a LOCAL tournament (game type is a session property, chosen here like stakes/size). */
  onSitTournament: (buyIn: '$100' | '$200') => void;
  /** how many seats the table you sit down at has (TB3 / TB0 §9 F1). */
  size: TableSize;
  onSize: (size: TableSize) => void;
  /** CX5 - which stakes level you sit down at ($1/$3 loose-passive, $3/$5 a step tougher). */
  stakesId: StakesId;
  onStakes: (id: StakesId) => void;
  /** CX5 - how many big blinds deep you buy in (75 / 100 / 200). Real dollars scale with the stakes. */
  buyInDepthBB: number;
  onBuyInDepth: (bb: number) => void;
  /** open the import-a-hand review surface. */
  onImport: () => void;
  /** open the progress dashboard. */
  onProgress: () => void;
}

export function Splash({
  onSit,
  onSitTournament,
  size,
  onSize,
  stakesId,
  onStakes,
  buyInDepthBB,
  onBuyInDepth,
  onImport,
  onProgress,
}: SplashProps) {
  const pool = STAKES_POOLS[stakesId];
  return (
    <>
      <div className="sp-arc" aria-hidden="true" />
      <div className="sp-wrap">
        <div className="sp-hero">
          <img className="sp-mark" src="/wordmark.png" alt="Postflop" />
          <div className="sp-tag">
            Read the table. <b>Play the game.</b>
          </div>
        </div>

        <section className="sp-sect">
          <div className="sp-eyebrow">Choose a game</div>
          <div className="sp-tiles">
            <button
              type="button"
              className="sp-tile active"
              aria-pressed="true"
              onClick={() => onSit('mid')}
            >
              <div className="sp-row1">
                <span className="sp-kick">Cash game</span>
                <span className="sp-badge on">Ready</span>
              </div>
              <div className="sp-name">Local &middot; {pool.label}</div>
              <div className="sp-sub">
                Read live players. Observe, diagnose, then solo the read, the price, and the play.
              </div>
              <span className="sp-go">&rarr;</span>
            </button>

            {/* ST-UI: the Tournament tile is now playable (was honestly locked "Later phase" until the cash
                v1 shipped). Game type is a property of the game you sit down at, so it is chosen HERE, like
                stakes and table size - not toggled mid-hand. Each buy-in sits you down at a local MTT. */}
            <div className="sp-tile tourney active">
              <div className="sp-row1">
                <span className="sp-kick">Tournament</span>
                <span className="sp-badge on">Ready</span>
              </div>
              <div className="sp-name">Local &middot; $100 / $200</div>
              <div className="sp-sub">
                Rising blinds, shrinking stacks, ICM pressure. Learn to read the condition the way cash teaches reading the player.
              </div>
              <div className="sp-tbuys">
                <button type="button" className="sp-tbuy" onClick={() => onSitTournament('$100')}>
                  $100 <span>MTT &rarr;</span>
                </button>
                <button type="button" className="sp-tbuy" onClick={() => onSitTournament('$200')}>
                  $200 <span>MTT &rarr;</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* CX5 - stakes. The load-bearing choice: $1/$3 is loose-passive (whales, stations), $3/$5 a step
            tougher (competent regs). The pool NOTE below the segment says how the table plays, honestly - the
            difference is the villain mix + their ranges, not just the numbers. */}
        <section className="sp-sect">
          <div className="sp-eyebrow">Stakes</div>
          <div className="sp-seg2" role="group" aria-label="Stakes">
            {STAKES_ORDER.map((id) => (
              <button
                key={id}
                type="button"
                className={'sp-size' + (id === stakesId ? ' sel' : '')}
                aria-pressed={id === stakesId}
                onClick={() => onStakes(id)}
              >
                <span className="sp-sn">{STAKES_POOLS[id].label}</span>
                <span className="sp-sl">{STAKES_POOLS[id].caption}</span>
              </button>
            ))}
          </div>
          <div className="sp-note">{pool.note}</div>
        </section>

        {/* CX5 - buy-in depth. Same BB ladder for every stakes level; the REAL DOLLARS scale with the stakes
            you picked (100bb = $300 at $1/$3, $500 at $3/$5) - "my money on the line", visibly. */}
        <section className="sp-sect">
          <div className="sp-eyebrow">Buy-in</div>
          <div className="sp-sizes" role="group" aria-label="Buy-in depth">
            {BUY_IN_DEPTHS.map((d) => (
              <button
                key={d.bb}
                type="button"
                className={'sp-size' + (d.bb === buyInDepthBB ? ' sel' : '')}
                aria-pressed={d.bb === buyInDepthBB}
                onClick={() => onBuyInDepth(d.bb)}
              >
                <span className="sp-sn">{dollars(buyInFor(stakesId, d.bb))}</span>
                <span className="sp-sl">
                  {d.bb} BB &middot; {d.label}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* TB3 - table size (TB0 §9 F1). Sits with the game choice, not the sit-down mode, because it is a
            property of the GAME you are picking; mode is only how much of the table you have already read. */}
        <section className="sp-sect">
          <div className="sp-eyebrow">Table size</div>
          <div className="sp-sizes" role="group" aria-label="Table size">
            {TABLE_SIZES.map((n) => (
              <button
                key={n}
                type="button"
                className={'sp-size' + (n === size ? ' sel' : '')}
                aria-pressed={n === size}
                onClick={() => onSize(n)}
              >
                <span className="sp-sn">{n}</span>
                <span className="sp-sl">handed</span>
              </button>
            ))}
          </div>
          <div className="sp-note">{SIZE_NOTE[size]}</div>
        </section>

        <section className="sp-sect">
          <div className="sp-eyebrow">Sit down at</div>
          <div className="sp-duo">
            <button type="button" className="sp-card newt" onClick={() => onSit('new')}>
              <div className="sp-ic" aria-hidden="true">&#9673;</div>
              <div className="sp-cn">New table</div>
              <div className="sp-cs">Nobody&apos;s tagged yet. Read every seat from a cold start, like a pro walking up.</div>
              <div className="sp-seatstrip" aria-hidden="true">
                <span className="sp-seat">?</span>
                <span className="sp-seat">?</span>
                <span className="sp-seat">?</span>
                <span className="sp-seat">?</span>
                <span className="sp-seat">?</span>
              </div>
            </button>

            <button type="button" className="sp-card mid" onClick={() => onSit('mid')}>
              <div className="sp-ic" aria-hidden="true">&#9632;</div>
              <div className="sp-cn">Mid&#8209;session</div>
              <div className="sp-cs">A few players already diagnosed, a few still unread. Drop into a live table.</div>
              <div className="sp-seatstrip" aria-hidden="true">
                <span className="sp-seat read">R</span>
                <span className="sp-seat">?</span>
                <span className="sp-seat vil">V</span>
                <span className="sp-seat read">F</span>
                <span className="sp-seat">?</span>
              </div>
            </button>
          </div>
          <div className="sp-note">Either way, a fresh hand is dealt every time you sit down.</div>
        </section>

        <div className="sp-foot">
          <button type="button" onClick={onImport}>&#8681;&nbsp; Import a hand</button>
          <button type="button" onClick={onProgress}>&#9678;&nbsp; Progress</button>
        </div>
      </div>
    </>
  );
}
